import type { CommentPinData } from '@corner/shared';
import { APP } from './tokens';

interface CommentThreadListProps {
  pins: CommentPinData[];
  activePinId?: string | null;
  onPinClick?: (pinId: string) => void;
}

const F = APP.fontFamily;

/**
 * Renders comment thread cards for the Comments tab in the right panel.
 * Three visual states: resolved (dimmed), open (border), active (orange border).
 */
export default function CommentThreadList({ pins, activePinId, onPinClick }: CommentThreadListProps) {
  const openCount = pins.filter((p) => p.status === 'open').length;
  const resolvedCount = pins.filter((p) => p.status === 'resolved').length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0, overflow: 'auto' }}>
      {/* Filter header */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 500, color: APP.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: F }}>
          All threads
        </span>
        <div style={{ flexGrow: 1, height: 1, backgroundColor: APP.border }} />
        <span style={{ fontSize: 11, fontWeight: 400, color: APP.textMuted, fontFamily: F }}>
          {openCount} open{resolvedCount > 0 ? ` · ${resolvedCount} resolved` : ''}
        </span>
      </div>

      {/* Thread cards */}
      <div style={{ display: 'flex', flexDirection: 'column', padding: '0 12px', gap: 6 }}>
        {pins.length === 0 && (
          <div style={{ padding: '32px 16px', textAlign: 'center', fontSize: 13, color: APP.textMuted, fontFamily: F }}>
            No comments yet. Click on the document to add one.
          </div>
        )}
        {pins.map((pin) => {
          const isActive = pin.pinId === activePinId;
          const isResolved = pin.status === 'resolved';
          const lastComment = pin.comments[pin.comments.length - 1];
          const firstComment = pin.comments[0];
          const replyCount = pin.comments.length - 1;

          return (
            <button
              key={pin.pinId}
              type="button"
              onClick={() => onPinClick?.(pin.pinId)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                padding: 12,
                borderRadius: 10,
                gap: 8,
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: F,
                border: isActive ? '1.5px solid #D97645' : isResolved ? 'none' : `1px solid ${APP.border}`,
                backgroundColor: isActive ? APP.bgCardBody : isResolved ? APP.bgCodeBlock : APP.bgCardBody,
                opacity: isResolved ? 0.6 : 1,
              }}
            >
              {/* Header row: icon + title + status */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {isResolved ? (
                  <div style={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: APP.textMuted, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                      <path d="M2 4l1.5 1.5 3-3.5" stroke="#FFF" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                ) : replyCount > 0 ? (
                  <div style={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: '#E05A33', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: 9, fontWeight: 700, color: '#FFFFFF' }}>{pin.comments.length}</span>
                  </div>
                ) : (
                  <div style={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: '#D97645', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                      <path d="M1 1.5h6A.5.5 0 017.5 2v3a.5.5 0 01-.5.5H3.5L1 7V2A.5.5 0 011.5 1.5H1Z" fill="#FFF" />
                    </svg>
                  </div>
                )}
                <span style={{ fontSize: 12, fontWeight: 500, color: isResolved ? APP.textCardDetail : APP.textPrimary, flexGrow: 1 }}>
                  {pin.sectionRef ?? `Page ${pin.pageNumber}`}
                </span>
                <span style={{ fontSize: 10, fontWeight: 400, color: isActive ? '#D97645' : APP.textMuted }}>
                  {isResolved ? 'Resolved' : isActive ? 'Active' : replyCount > 0 ? `${replyCount} ${replyCount === 1 ? 'reply' : 'replies'}` : ''}
                </span>
              </div>

              {/* Quote block — shown when comment is anchored to selected text */}
              {pin.quote && (
                <div style={{ borderLeft: '2px solid #D97645', paddingLeft: 8, paddingBlock: 4 }}>
                  <span style={{
                    fontSize: 9, fontWeight: 500, letterSpacing: '0.05em',
                    color: '#D97645', textTransform: 'uppercase' as const, fontFamily: F,
                    display: 'block', paddingBottom: 2,
                  }}>
                    Quote
                  </span>
                  <span style={{
                    fontSize: 11, fontWeight: 400, color: APP.textCardDetail,
                    lineHeight: '16px', fontFamily: '"Georgia", serif', display: 'block',
                  }}>
                    {pin.quote.length > 200 ? pin.quote.slice(0, 200) + '…' : pin.quote}
                  </span>
                </div>
              )}

              {/* Body text — first comment */}
              {firstComment && (
                <span style={{ fontSize: 12, fontWeight: 400, color: isResolved ? APP.textMuted : APP.textCardDetail, lineHeight: '16px' }}>
                  {firstComment.body}{firstComment.authorName ? ` — ${firstComment.authorName}` : ''}
                </span>
              )}

              {/* Reply preview (active thread only) */}
              {!isResolved && replyCount > 0 && lastComment && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, paddingTop: 4, borderTop: `1px solid ${APP.border}` }}>
                  <div style={{
                    width: 14, height: 14, borderRadius: '50%',
                    backgroundColor: lastComment.authorColor || '#8B7355',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <span style={{ fontSize: 6, fontWeight: 600, color: '#FFFFFF' }}>
                      {initials(lastComment.authorName)}
                    </span>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 400, color: APP.textMuted }}>
                    {lastComment.authorName} replied {timeAgo(lastComment.createdAt)}
                  </span>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name[0]?.toUpperCase() ?? '?';
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}
